'use client'

/**
 * CustomMpgInput — lets a tour manager override the vehicle preset's base MPG
 * with their actual measured fuel economy.
 *
 * UX model:
 *   - Collapsed state (default): a subtle "Using preset 6 MPG · Override" strip
 *     that doesn't clutter the form when users are fine with defaults.
 *   - Expanded state (after clicking Override): a number input with the preset
 *     shown as placeholder. Clear/reset link returns to preset.
 *   - Changing vehicles resets back to collapsed state (store's setVehicle
 *     nulls out customMpg automatically).
 *
 * Validation:
 *   - Range 1-50 MPG covers every realistic touring vehicle (from a 4 MPG
 *     semi to a 48 MPG hybrid sedan). Out-of-range inputs are ignored —
 *     the store only updates when a valid number arrives.
 *
 * Downstream integration:
 *   - The store's `calculate()` already uses `customMpg ?? vehicle.baseMpg`
 *     as the base input to the grade-adjustment math — no engine changes.
 */

import { Check, Pencil, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { track } from '@/lib/analytics'
import type { VehicleId } from '@/lib/types'
import { getVehicle } from '@/lib/vehicles'

interface CustomMpgInputProps {
	vehicleId: VehicleId
	value: number | null
	onChange: (mpg: number | null) => void
}

export function CustomMpgInput({ vehicleId, value, onChange }: CustomMpgInputProps) {
	const vehicle = getVehicle(vehicleId)
	const [isEditing, setIsEditing] = useState(value !== null)
	const [inputValue, setInputValue] = useState(value === null ? '' : String(value))

	// If vehicle changes (which nulls customMpg in the store), collapse the UI
	useEffect(() => {
		if (value === null) {
			setIsEditing(false)
			setInputValue('')
		}
	}, [value])

	const handleChange = (raw: string) => {
		setInputValue(raw)
		if (raw === '') {
			onChange(null)
			return
		}
		const parsed = Number.parseFloat(raw)
		if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 50) {
			// Only track the first time a user sets a valid override in a session
			// — the input debounces as they type, so we'd otherwise fire per keystroke
			if (value === null) {
				track('OverrodeMPG', { vehicle: vehicleId, mpg_value: parsed })
			}
			onChange(parsed)
		}
	}

	const handleReset = () => {
		setIsEditing(false)
		setInputValue('')
		onChange(null)
	}

	/**
	 * Done button closes the editor. The value is ALREADY saved to the
	 * store via onChange-per-keystroke (live-binding); Done is purely a
	 * UX affordance so users know the number is committed, not a separate
	 * save step. If they typed an invalid value and haven't corrected it,
	 * Done is disabled so they can't dismiss a bad input.
	 */
	const handleDone = () => {
		if (value !== null) setIsEditing(false)
	}

	const hasCustom = value !== null

	if (!isEditing) {
		return (
			<div className="flex items-center justify-between gap-2">
				<div className="text-sm">
					<span className="text-text-secondary">Base MPG: </span>
					<span className="font-mono font-semibold text-text-primary">
						{hasCustom ? value : vehicle.baseMpg}
					</span>
					<span
						className={`ml-1.5 text-[10px] uppercase tracking-wider ${hasCustom ? 'text-brand' : 'text-text-muted'}`}
					>
						{hasCustom ? 'custom' : 'preset'}
					</span>
				</div>
				<div className="flex items-center gap-1.5 shrink-0">
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-brand/60 hover:text-text-primary"
					>
						<Pencil className="size-3" aria-hidden />
						{hasCustom ? 'Edit' : 'Override'}
					</button>
					{hasCustom && (
						<button
							type="button"
							onClick={handleReset}
							className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-muted transition-colors hover:border-error-fg/60 hover:text-error-fg"
							title={`Reset to preset (${vehicle.baseMpg} MPG)`}
						>
							<RotateCcw className="size-3" aria-hidden />
							Reset
						</button>
					)}
				</div>
			</div>
		)
	}

	return (
		<fieldset className="rounded-md border border-brand/30 bg-brand/5 px-3 py-2.5">
			<legend className="flex w-full items-center justify-between text-sm">
				<span className="font-semibold text-text-primary">Custom Base MPG</span>
				<button
					type="button"
					onClick={handleReset}
					className="flex items-center gap-1 text-xs text-text-muted hover:text-text-primary transition-colors"
				>
					<RotateCcw className="size-3" aria-hidden />
					Use preset ({vehicle.baseMpg})
				</button>
			</legend>
			<div className="mt-2 flex items-center gap-2">
				<Input
					type="number"
					inputMode="decimal"
					min={1}
					max={50}
					step={0.1}
					value={inputValue}
					onChange={(e) => handleChange(e.target.value)}
					placeholder={String(vehicle.baseMpg)}
					className="font-mono flex-1"
					aria-label="Custom base MPG"
				/>
				<Button
					type="button"
					variant="default"
					size="sm"
					onClick={handleDone}
					disabled={!hasCustom}
					title={hasCustom ? 'Close editor (value is already saved)' : 'Enter a valid MPG (1–50)'}
				>
					<Check className="size-3.5" aria-hidden />
					Done
				</Button>
			</div>
			<p className="mt-1.5 text-[10px] text-text-muted">
				Grade adjustments apply on top of your custom base. Saved automatically as you type.
			</p>
		</fieldset>
	)
}
